import { useStudioLocale } from '@/i18n/use-studio-locale'
import { PREVIEW_REVIEW_COPY } from './preview-review-outcome-copy'
import {
  usePreviewReviewOutcome,
  type PreviewReviewOutcomeKind,
} from './preview-review-outcome-store'

/**
 * What the automatic visual review did with the screen in front of the creator.
 *
 * Renders nothing until a review has run — there is no outcome to report and a
 * note that is always present is noise. Every non-success case gets its own
 * sentence, because "refused", "not offered here", "could not be captured" and
 * "could not be sent" have different remedies and used to be indistinguishable
 * from success.
 */
export function PreviewReviewOutcomeNote() {
  const { locale } = useStudioLocale()
  const outcome = usePreviewReviewOutcome()
  if (outcome === null) return null

  const copy = PREVIEW_REVIEW_COPY[locale]
  // A Record keyed by the union, not a ternary chain with a default: a chain
  // sends every kind it does not name into `failed`, which is how two real
  // give-ups arrived on screen wearing a transport error's sentence. Adding a
  // kind now fails to compile until it has words of its own.
  const sentence: Record<PreviewReviewOutcomeKind, () => string> = {
    reviewed: () => copy.reviewed,
    unauthorized: () => copy.unauthorized,
    unavailable: () => copy.unavailable,
    'capture-failed': () => copy.captureFailed,
    'surface-not-rendered': () => copy.surfaceNotRendered,
    failed: () => copy.failed(outcome.status ?? 0),
  }
  const text = sentence[outcome.kind]()

  return (
    <span
      data-testid="preview-review-outcome"
      className={`truncate text-xs ${
        outcome.kind === 'reviewed' ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400'
      }`}
    >
      {text}
    </span>
  )
}
